"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TYPES = [
  { type: "Asset", categories: "Other Asset, Other Current Asset, Cash, Bank, Fixed Asset, Accounts Receivable, Stock, Payment Clearing Account, Intangible Asset, Non Current Asset, Deferred Tax Asset" },
  { type: "Liability", categories: "Other Current Liability, Non Current Liability, Other Liability, Accounts Payable, Overseas Tax Payable, Deferred Tax Liability" },
  { type: "Equity", categories: "Equity" },
  { type: "Income", categories: "Income, Other Income" },
  { type: "Expense", categories: "Expense, Cost of Goods Sold, Other Expense" },
];

const RULES = [
  {
    topic: "Subscription / permission",
    rule: "Accountant Chart of Accounts create, edit, and delete. Viewing the list and the account statement is view",
  },
  {
    topic: "No 4-digit auto code",
    rule: "Account Code is optional free text. Nothing auto-numbers CoA. Seeded system accounts often have no code",
  },
  {
    topic: "Bank is not created here",
    rule: "Category Bank is managed in Banking. Add Account shows Continue to Banking and does not save a CoA row. Delete is also refused — delete from Banking → Banks",
  },
  {
    topic: "Opening balance only on some categories",
    rule: "Balance-sheet categories with openingBalanceIn CoA: optional amount > 0, Balance Type Debit or Credit, Opening Balance Date. Income, Other Income, Expense, COGS, Other Expense have no CoA OB. Bank OB is on Banking Add Bank",
  },
  {
    topic: "OB posts a journal",
    rule: "Saving OB deletes any prior Opening Balance journal and posts against Opening Balance Adjustments. Debit OB: Dr this account / Cr Opening Balance Adjustments. Credit OB is the reverse. Narration: Opening balance. Not per branch. You can change or clear later — it is not locked after other transactions",
  },
  {
    topic: "Sub-accounts",
    rule: "Make this a sub-account + Parent Account, same category. Bank, Payment Clearing, Deferred Tax Asset/Liability, and Overseas Tax Payable cannot be sub-accounts. An account that already has children cannot become a sub-account. Closing Balance on the list is this account only — parents do not roll up children",
  },
  {
    topic: "Lock means system account",
    rule: "The lock icon is isSystemAccount from company seed. There is no user Lock / Unlock and no period freeze on this screen. You can rename and set code / OB. You cannot delete, change category, parent, or Active",
  },
  {
    topic: "Active",
    rule: "Edit only, non-system. Uncheck Active is blocked while the account still has active sub-accounts. The list shows active accounts only",
  },
  {
    topic: "Delete",
    rule: "Hidden for system and Banking-managed accounts. Blocked if the account is used on ledger, manual journals, items, or contacts. Soft-deletes (isActive false) and reparents children",
  },
  {
    topic: "Account statement is row click",
    rule: "Click a CoA row to open Date, Transaction Details, Document No., Type, Debit, Credit plus Closing Balance / Total Debit / Total Credit. That is not the sidebar Account Ledger module",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating an account" },
      { heading: "2. Categories" },
      { heading: "3. Sub-accounts" },
      { heading: "4. Opening balance" },
      { heading: "5. Banking-managed accounts" },
      { heading: "6. System accounts and the lock" },
      { heading: "7. The list and closing balance" },
      { heading: "8. Account statement (row click)" },
      { heading: "9. Editing, Active, and deleting" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function ChartOfAccountsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Chart of Accounts
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone adding GL accounts, setting opening balances, and
            reading what posted to an account
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            The <B>Chart of Accounts</B> is the tree of general-ledger
            accounts. Invoices, bills, payments, and journals post here.{" "}
            <B>Add Account</B> opens a modal. <B>Bank</B> accounts are
            created under Banking → Banks, not on this form. The lock icon
            is a <B>system account</B> from company setup — not a period
            freeze. Click a row to open that account&apos;s statement.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating an account
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            List header <B>Add Account</B> (Accountant create). Modal title{" "}
            <B>Add Account</B> / <B>Edit Account</B>. Buttons{" "}
            <B>Cancel</B> and <B>Add Account</B> (create) or <B>Save</B>{" "}
            (edit). The same modal can open from Expenses when you add an
            account there.
          </p>
          <Definitions
            items={[
              {
                term: "Category",
                desc: (
                  <>
                    Required. Grouped under Asset, Liability, Equity, Income,
                    Expense. You do not create categories — the list is
                    fixed.
                  </>
                ),
              },
              {
                term: "Account Name",
                desc: (
                  <>
                    Required, except when Category is Banking-managed (then
                    the name field is hidden). Duplicate name in the same
                    category (active) is blocked.
                  </>
                ),
              },
              {
                term: "Account Code",
                desc: <>Optional. Not auto-generated.</>,
              },
              {
                term: "Make this a sub-account / Parent Account",
                desc: <>See section 3.</>,
              },
              {
                term: "Opening Balance, Balance Type, Opening Balance Date",
                desc: <>See section 4. Hidden when the category has no CoA OB.</>,
              },
              {
                term: "Active",
                desc: <>Edit only, non-system accounts.</>,
              },
            ]}
          />
          <Callout type="note">
            There is no description, currency, watchlist, or branch on this
            form. Bank name / IFSC / primary live on Banking.
          </Callout>
          <Screenshot caption="Screenshot: Add Account modal with Category, Account Name, Account Code, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Categories
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Five account types. Credit Card is not a live CoA category.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Type
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Categories on the picker
                  </th>
                </tr>
              </thead>
              <tbody>
                {TYPES.map((t, i) => (
                  <tr
                    key={t.type}
                    className={
                      i < TYPES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[120px]">
                      {t.type}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.categories}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Category picker grouped by Asset, Liability, Equity, Income, Expense." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Sub-accounts
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Tick <B>Make this a sub-account</B>, then <B>Parent Account</B>{" "}
            — same category, active. Hidden if this account already has
            children.
          </p>
          <Definitions
            items={[
              {
                term: "Cannot be sub-accounts",
                desc: (
                  <>
                    Bank, Payment Clearing Account, Deferred Tax Asset,
                    Deferred Tax Liability, Overseas Tax Payable.
                  </>
                ),
              },
              {
                term: "System accounts as parents",
                desc: (
                  <>
                    Edit blocks using a system account as parent. Seeded
                    trees can already nest (for example Input Tax Credits →
                    Input CGST).
                  </>
                ),
              },
              {
                term: "Balances",
                desc: (
                  <>
                    List <B>Closing Balance</B> is this account&apos;s own
                    net (debit minus credit), not the sum of children.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Make this a sub-account with Parent Account selected." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Opening balance
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Optional on create/edit when the category allows CoA opening
            balance. Amount must be greater than zero. You pick{" "}
            <B>Debit</B> or <B>Credit</B> (default Debit) — it is not
            forced by Asset vs Liability. Date is required with the amount.
            One row per account, not per branch. No bulk CoA OB.
          </p>
          <Callout type="note">
            Income and expense categories have no opening balance on this
            form. Bank opening balance is on Banking → Add Bank (positive =
            Debit, negative = Credit).
          </Callout>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Save posts (or replaces) a journal against{" "}
            <B>Opening Balance Adjustments</B>, narration Opening balance.
            Clear by sending no amount. Changing category away from a CoA-OB
            category requires removing OB first.
          </p>
          <Screenshot caption="Screenshot: Opening Balance, Balance Type Debit/Credit, and Opening Balance Date on Add Account." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Banking-managed accounts
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Category <B>Bank</B>: banner plus <B>Continue to Banking</B> →
            /banks/new. Edit of a Bank row goes to /banks/:id/edit.
          </p>
          <Definitions
            items={[
              {
                term: "Cash / Petty Cash",
                desc: (
                  <>
                    Created on CoA. Company seed includes system{" "}
                    <B>Petty Cash</B>. Banking will not create a second
                    Petty Cash.
                  </>
                ),
              },
              {
                term: "Payment Clearing Account",
                desc: (
                  <>
                    Created on CoA. Seed includes <B>Undeposited Funds</B>.
                    Payments Received Deposit To can use Cash, Bank, Payment
                    Clearing, or Credit Card on the chart.
                  </>
                ),
              },
              {
                term: "Primary bank",
                desc: (
                  <>
                    <B>Make this primary</B> is on the Banking form, not
                    CoA. Quotes, Sales Orders, Delivery Challans, and
                    Invoices need one primary bank to create.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Account with Category Bank and Continue to Banking." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. System accounts and the lock
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Company setup seeds a standard set. Rows with{" "}
            <B>isSystemAccount</B> show a lock. Tooltip: you cannot delete,
            but you can edit account details (name, code, OB where allowed).
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Locked examples modules post to: Accounts Receivable, Accounts
            Payable, Sales, Inventory Asset, Cost of Goods Sold, Goods
            Received Not Invoiced, GST Payable and Input Tax Credits (with
            output/input CGST SGST IGST children), Petty Cash, Undeposited
            Funds, Unearned Revenue, Opening Balance Adjustments, Retained
            Earnings, Exchange Gain or Loss.
          </p>
          <Callout type="warning">
            Lock is not a month-end freeze. There is no Unlock. Some seeded
            names (Rent, Office Supplies, Furniture) are not system-locked
            and can be deleted if unused.
          </Callout>
          <Screenshot caption="Screenshot: Chart of Accounts list with lock icon on Accounts Receivable." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. The list and closing balance
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title Chart of Accounts with a count. Tree expand/collapse.
            Columns: Account Name, Account Code, Closing Balance (amount +
            Dr/Cr), Account Type (category display name), Parent Account,
            lock, Action. No search, no export, no category filter on this
            list.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Action: <B>Edit</B>, <B>Delete</B> (hidden for system and Bank).
            Delete confirm: Delete Account.
          </p>
          <Screenshot caption="Screenshot: Chart of Accounts tree with Closing Balance Dr/Cr." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Account statement (row click)
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Click a row. Header uses the account name. Stats:{" "}
            <B>Closing Balance</B>, <B>Total Debit</B>,{" "}
            <B>Total Credit</B>, <B>Account Type</B>. Columns:{" "}
            <B>Date</B>, <B>Transaction Details</B>, <B>Document No.</B>,{" "}
            <B>Type</B>, <B>Debit</B>, <B>Credit</B>.
          </p>
          <Callout type="warning">
            This statement is not Accountant → Account Ledger. That sidebar
            item is a named ledger master (people/GST) used on Expenses. GL
            movements live here, on the CoA row.
          </Callout>
          <Screenshot caption="Screenshot: account statement after clicking Accounts Receivable on the CoA list." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Editing, Active, and deleting
          </h3>
          <Definitions
            items={[
              {
                term: "Edit",
                desc: (
                  <>
                    Same modal. System accounts cannot change category,
                    parent, or Active. Bank rows redirect to Banking.
                  </>
                ),
              },
              {
                term: "Active",
                desc: (
                  <>
                    Uncheck to hide from the list. Blocked while active
                    sub-accounts exist.
                  </>
                ),
              },
              {
                term: "Delete",
                desc: (
                  <>
                    Soft-delete. Reparents children. Removes opening
                    balance. Blocked when used in transactions, journals,
                    items, or contacts.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Delete Account blocked because the account is used on a journal." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/banking/banks"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Banks <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where Bank accounts, primary, and bank opening balance are
              created
            </p>
          </Link>
          <Link
            href="/documentation/books/accountant/manual-journals"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Manual Journals <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              User entries that post to these accounts on Save
            </p>
          </Link>
          <Link
            href="/documentation/books/accountant/account-ledger"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Account Ledger <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Named ledgers for Expenses — not this CoA statement
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
